import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "FilterCartridges",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Filter Components",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Stainless Steel Filter Cartridges",
      lead: "Engineered cylindrical and pleated stainless steel filter cartridges fabricated for demanding high-temperature, high-differential pressure, and corrosive industrial liquid and gas separation systems.",
      secondaryCta: {
        label: "Filter Components Hub",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
        alt: "Precision pleated stainless steel cylindrical filter element with welded end caps",
      },
      cardNote: "All-welded & crimped OEM configurations",
      cardLink: {
        label: "Request Specifications",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Pore Rating", value: "1 µm – 250 µm" },
        { label: "Construction", value: "All-Welded / Crimped" },
        { label: "Element OD", value: "25 – 350 mm" },
        { label: "Standard Alloys", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Cartridge Architecture & Performance",
      heading: "Engineered Metallic Cartridges for Harsh Industrial Filtration",
      paragraphs: [
        "MutualFilter stainless steel filter cartridges provide robust, cleanable filtration barriers for processes where polymer-based disposable filter media degrade, collapse, or contaminate the stream. Fabricated exclusively from woven wire mesh, sintered metal mesh, and perforated structural cores, these elements withstand thermal extremes and severe pressure differentials.",
        "Cartridges are manufactured in two foundational architectures: smooth cylindrical tubes for high-velocity surface straining or cake collection, and pleated configurations that increase the active surface area up to three times within the same housing envelope. Precision longitudinal seam welding and robotic end-cap fusion guarantee zero media bypass and permanent structural stability.",
        "Available in standard industrial lengths and custom OEM lengths with diverse terminal fittings including double open-end (DOE) configurations, code 7 and code 8 single open-end (SOE) bayonets with fluoroelastomer O-rings, and threaded BSP or NPT couplings.",
      ],
      keyPoints: [
        "All-welded metallurgical seam and end-fitting construction eliminates adhesives, epoxy resins, and bypass risks.",
        "Pleated wire mesh architecture expands effective filtration surface up to 3x over smooth cylindrical cores for longer cycle times.",
        "Fully regenerable and reusable via ultrasonic cleaning, solvent baths, backflushing, and thermal burnout procedures.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/oil-filter-cartridge-stainless-steel-pleated-filter-element.webp?v=nwo72bdda2k",
        alt: "Pleated and sintered stainless steel filter cartridges in precision manufacturing workshop",
      },
    },
    specTable: {
      heading: "Standard Cartridge Dimensional & Retention Parameters",
      intro: "Representative technical specifications for all-welded and pleated stainless steel filter cartridges manufactured from AISI 304 and 316L wire mesh.",
      columns: [
        { key: "configuration", label: "Element Configuration", align: "left" },
        { key: "mediaType", label: "Filter Media Structure", align: "left" },
        { key: "retentionRange", label: "Retention Cutoff (µm)", align: "right" },
        { key: "outerDiameter", label: "Outer Diameter (mm)", align: "right" },
        { key: "standardLength", label: "Standard Length (mm)", align: "right" },
      ],
      rows: [
        {
          configuration: "Cylindrical Plain Element",
          mediaType: "Single / Multilayer Woven Mesh",
          retentionRange: "20 – 250",
          outerDiameter: "25 – 150",
          standardLength: "250 – 1,000",
        },
        {
          configuration: "Pleated High-Surface Element",
          mediaType: "Pleated Dutch / Square Mesh",
          retentionRange: "5 – 150",
          outerDiameter: "50 – 250",
          standardLength: "250 – 1,000",
        },
        {
          configuration: "Sintered Mesh Cartridge",
          mediaType: "5-Layer / Sintered Laminate",
          retentionRange: "1 – 100",
          outerDiameter: "30 – 200",
          standardLength: "125 – 1,500",
        },
        {
          configuration: "Heavy-Duty Core Element",
          mediaType: "Perforated Core + Woven Wrap",
          retentionRange: "10 – 200",
          outerDiameter: "60 – 350",
          standardLength: "500 – 2,000",
        },
      ],
      caption: "Custom element diameters (up to 350 mm), custom lengths (up to 3,000 mm), and bespoke end adapters fabricated on request.",
    },
    features: {
      heading: "Key Engineering Attributes of MutualFilter Cartridges",
      intro: "Precision manufacturing standards tailored to continuous operational reliability and extended maintenance cycles.",
      items: [
        {
          title: "Rigid Structural Cores",
          text: "Heavy-gauge perforated or slotted stainless steel inner cores provide high collapse resistance under elevated differential pressures and reverse surges.",
        },
        {
          title: "Custom End-Cap Fittings",
          text: "Precision-machined DOE flat gaskets, SOE bayonet plugs with dual O-rings, threaded couplings, and sanitary flanges welded directly to the media envelope.",
        },
        {
          title: "Uniform Pleating Control",
          text: "Calibrated pleat spacing and internal wire mesh support layers prevent pleat pinch-off, ensuring unobstructed fluid flow across the complete media surface.",
        },
        {
          title: "100% Metallic Integrity",
          text: "Manufactured strictly without binders, adhesives, or elastomers in the flow path, ensuring maximum compatibility with aggressive solvents, steam, and corrosive acids.",
        },
      ],
    },
    related: {
      heading: "Related Filter Components & Fabricated Assemblies",
      intro: "MutualFilter supplies complementary stainless steel filtration assemblies engineered for integration into industrial vessels, pipelines, and extruders.",
      cards: [
        {
          title: "Filter Components Hub",
          text: "Overview of fabricated stainless steel filtration elements, component engineering capabilities, and custom manufacturing ranges.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Fabricated stainless steel filter components and assemblies overview",
          },
          badge: "Parent Category",
          linkLabel: "Explore Components",
        },
        {
          title: "Precision Slit Coils & Filter Discs",
          text: "Single and multilayer wire mesh filter discs and narrow slit coils stamped to tight tolerances for screen changers and inline filter bodies.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Precision stamped stainless steel filter discs and slit wire mesh coils",
          },
          badge: "Sibling Element",
          linkLabel: "View Filter Discs",
        },
        {
          title: "Precision Filter Tubes & Cylinders",
          text: "Rigid welded cylindrical screen tubes and multi-tube assemblies engineered for pipeline strainers, well intakes, and liquid-gas separation.",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Precision welded stainless steel filter tubes and cylinders",
          },
          badge: "Sibling Element",
          linkLabel: "View Filter Tubes",
        },
        {
          title: "Multi-Layer Screen Pack Assemblies",
          text: "Spot-welded, aluminum-rimmed, and sintered multi-layer wire mesh screen packs engineered for extruder screen changers and polymer melt filtration.",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Multilayer rimmed and spot-welded wire mesh filter packs",
          },
          badge: "Sibling Element",
          linkLabel: "View Screen Packs",
        },
        {
          title: "Fabricated Filter Baskets & Plates",
          text: "Heavy-duty wire mesh filter baskets, strainer baskets, and flat filter plates engineered for industrial vessel clarification and pressure filtration.",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Fabricated stainless steel filter basket for vessel strainers",
          },
          badge: "Sibling Element",
          linkLabel: "View Baskets & Plates",
        },
      ],
    },
    cta: {
      id: "filter-cartridges-cta",
      eyebrow: "Custom Element Engineering",
      heading: "Request Custom Cartridge Specifications or Production Pricing",
      lead: "Provide your required cartridge dimensions, retention rating, connection style, and process operating parameters for technical review and quotation.",
      secondaryLabel: "Contact Engineering Team",
      secondaryTo: "/ContactPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "FilterCartridges",
    relatedTopic: "components",
    hero: {
      categoryBadge: "过滤组件与元件",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "不锈钢滤芯与滤筒组件",
      lead: "工程级不锈钢圆柱及折叠滤芯元件，专为高温、高压差以及严苛腐蚀性工业液体与气体分离工艺量身定制。",
      secondaryCta: {
        label: "过滤组件中心",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
        alt: "精密折叠不锈钢圆柱滤芯与全焊接端盖结构",
      },
      cardNote: "全焊接与机械滚压 OEM 定制构造",
      cardLink: {
        label: "索取技术参数",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "过滤精度", value: "1 µm – 250 µm" },
        { label: "制造结构", value: "全焊接 / 机械滚压卷边" },
        { label: "外径范围", value: "25 – 350 mm" },
        { label: "标准材质", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "结构架构与过滤性能",
      heading: "适用于极端工业工况的工程级金属滤芯",
      paragraphs: [
        "MutualFilter 不锈钢滤芯为严苛流体过滤提供坚固、可再生的金属过滤屏障，专用于聚合物一次性滤芯易受热降解、压溃变形或溶出杂质的高要求工艺。滤芯精选不锈钢编织丝网、多层金属烧结网以及加固冲孔内胆精工制造，具有卓越的高温承受力与耐压差强度。",
        "滤芯主要分为两大结构形式：光滑圆柱滤筒适用于高流速表面拦截或滤饼截留；折叠式滤芯在相同滤壳空间内可将有效过滤表面积扩展高达 3 倍，大幅延长运行周期并降低初始压差。高精度纵向接缝熔焊与端盖全熔焊工艺杜绝旁路泄漏风险，结构稳定可靠。",
        "可提供各类标准工业长度与定制规格，端盖接口涵盖平垫双开口 (DOE)、带双密封圈的 222/226 卡口单开口 (SOE)、内/外管螺纹接口以及卫生级卡箍法兰，满足不同过滤器壳体的严密安装要求。",
      ],
      keyPoints: [
        "全焊接冶金熔融接缝与端盖结构，杜绝胶粘剂与环氧树脂溶出污染，消除旁路泄漏风险。",
        "折叠丝网结构相比同尺寸光滑圆柱中心筒提供高达 3 倍有效过滤面积，延长滤芯在线寿命。",
        "支持超声波清洗、溶剂浸泡、在线反冲洗及高温热解再生，实现长期循环重复利用。",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/oil-filter-cartridge-stainless-steel-pleated-filter-element.webp?v=nwo72bdda2k",
        alt: "精密制造车间内的折叠式与烧结不锈钢滤芯组件",
      },
    },
    specTable: {
      heading: "标准滤芯几何尺寸与过滤精度参数",
      intro: "采用 AISI 304 及 316L 材质编织网与烧结网精工制造的全焊接及折叠不锈钢滤芯代表性技术规格。",
      columns: [
        { key: "configuration", label: "滤芯结构配置", align: "left" },
        { key: "mediaType", label: "主滤材形式", align: "left" },
        { key: "retentionRange", label: "截留精度 (µm)", align: "right" },
        { key: "outerDiameter", label: "元件外径 (mm)", align: "right" },
        { key: "standardLength", label: "标准长度 (mm)", align: "right" },
      ],
      rows: [
        {
          configuration: "圆柱形光滑滤芯",
          mediaType: "单层 / 多层编织丝网",
          retentionRange: "20 – 250",
          outerDiameter: "25 – 150",
          standardLength: "250 – 1,000",
        },
        {
          configuration: "高面积折叠滤芯",
          mediaType: "折叠密纹网 / 平织网",
          retentionRange: "5 – 150",
          outerDiameter: "50 – 250",
          standardLength: "250 – 1,000",
        },
        {
          configuration: "多层金属烧结滤芯",
          mediaType: "5层标准烧结复合网板",
          retentionRange: "1 – 100",
          outerDiameter: "30 – 200",
          standardLength: "125 – 1,500",
        },
        {
          configuration: "重载骨架增强滤芯",
          mediaType: "冲孔内胆 + 外包覆编织网",
          retentionRange: "10 – 200",
          outerDiameter: "60 – 350",
          standardLength: "500 – 2,000",
        },
      ],
      caption: "可按需定制最大外径 350 mm、最大长度 3,000 mm 的非标滤芯及专用异形接头。",
    },
    features: {
      heading: "MutualFilter 工业不锈钢滤芯核心技术特性",
      intro: "严格的精密制造工艺保障产品在连续工况下的高可靠性与更长维护周期。",
      items: [
        {
          title: "坚固冲孔支撑内胆",
          text: "厚壁不锈钢冲孔板或螺旋骨架支撑管，在剧烈压差脉冲与介质反向冲击下提供高抗压溃能力。",
        },
        {
          title: "多样化端盖接头形式",
          text: "精密数控加工 DOE 平垫密封端盖、带双氟胶 O 型圈的 SOE 插入式端盖、螺纹接头及快装法兰，与滤网全焊连接。",
        },
        {
          title: "均匀折叠波距控制",
          text: "精密波距均布工艺搭配内外金属支撑网层，有效防止流体压力下的波纹贴合粘并，保持流道畅通。",
        },
        {
          title: "100% 全金属材质洁净度",
          text: "流道内部不含任何胶粘剂、树脂粘接剂或可溶出聚合物，与高温蒸汽、有机溶剂及酸碱介质具备优良兼容性。",
        },
      ],
    },
    related: {
      heading: "相关过滤组件与丝网装配制品",
      intro: "MutualFilter 提供完善的不锈钢金属过滤组件体系，满足压力容器、工业管路及熔体挤出换网器的多样化配套需求。",
      cards: [
        {
          title: "过滤组件产品中心",
          text: "全面了解不锈钢丝网精加工组件系列、精密加工工艺能力与定制参数范围。",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "不锈钢过滤组件与加工元件总览",
          },
          badge: "母分类导航",
          linkLabel: "浏览组件中心",
        },
        {
          title: "精密分条网带与冲压滤圆片",
          text: "单层及多层丝网冲压滤圆片与连续高精度分条网带，适配换网器及管路精密过滤器。",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "精密不锈钢冲压滤圆片与分条不锈钢丝网卷",
          },
          badge: "同级组件",
          linkLabel: "查看滤圆片与网带",
        },
        {
          title: "精密滤管与圆筒滤网组件",
          text: "高刚性纵缝全焊不锈钢滤筒与多管式过滤元件，专用于管路过滤器与固液分离系统。",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "精密焊接不锈钢滤管与圆筒滤芯组件",
          },
          badge: "同级组件",
          linkLabel: "查看滤管与圆筒",
        },
        {
          title: "挤出机换网器多层组合滤网",
          text: "精密点焊、铝包边及烧结多层组合滤网，专为聚合物熔体挤出、共混造粒及塑料回收换网器设计。",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "多层包边及点焊不锈钢组合滤网",
          },
          badge: "同级组件",
          linkLabel: "查看组合滤网",
        },
        {
          title: "不锈钢滤篮与平板滤板组件",
          text: "重载冲孔骨架丝网滤篮与精密平板滤板，专用于大通量容器澄清、叶片压滤及管道杂质拦截。",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "用于管道容器过滤器的精密不锈钢编织丝网滤篮",
          },
          badge: "同级组件",
          linkLabel: "查看滤篮与滤板",
        },
      ],
    },
    cta: {
      id: "filter-cartridges-cta",
      eyebrow: "定制滤芯工程支持",
      heading: "索取定制不锈钢滤芯规格参数与生产报价",
      lead: "欢迎提供滤芯外形几何尺寸、目标过滤精度、端盖接头形式及工况介质要求，技术团队将为您迅速出具方案与报价。",
      secondaryLabel: "联系工程技术团队",
      secondaryTo: "/ContactPage",
      note: "材质检测报告 (MTR) 与正向材质识别 (PMI) 可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "FilterCartridges",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Componentes filtrantes",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Cartuchos filtrantes de acero inoxidable",
      lead: "Cartuchos filtrantes cilíndricos y plisados de acero inoxidable, diseñados para sistemas de separación de líquidos y gases industriales bajo altas temperaturas y elevadas presiones diferenciales.",
      secondaryCta: {
        label: "Centro de componentes",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
        alt: "Elemento filtrante cilíndrico plisado de acero inoxidable con tapas terminales soldadas",
      },
      cardNote: "Construcciones totalmente soldadas o engastadas OEM",
      cardLink: {
        label: "Solicitar especificaciones",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Rango de micras", value: "1 µm – 250 µm" },
        { label: "Construcción", value: "Totalmente soldado / Engastado" },
        { label: "Diámetro exterior", value: "25 – 350 mm" },
        { label: "Aleaciones", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Arquitectura y rendimiento del cartucho",
      heading: "Cartuchos metálicos diseñados para filtración industrial severa",
      paragraphs: [
        "Los cartuchos de acero inoxidable MutualFilter proporcionan barreras de filtración robustas y lavables para procesos en los que los medios desechables de polímero se degradan, colapsan o contaminan el fluido. Fabricados a partir de malla tejida, malla sinterizada y núcleos perforados, resisten cambios térmicos y altas presiones.",
        "Se configuran en dos geometrías básicas: tubos cilíndricos lisos para retención superficial rápida, y elementos plisados que multiplican el área de filtración activa hasta tres veces dentro de la misma carcasa. La soldadura longitudinal de precisión y la fusión de terminales aseguran total estanqueidad.",
        "Disponibles en longitudes estándar y a medida con terminales DOE (doble extremo abierto), bayoneta SOE con juntas tóricas y conexiones roscadas BSP o NPT.",
      ],
      keyPoints: [
        "Construcción mecano-soldada que elimina adhesivos y resinas epoxi, anulando riesgos de bypass.",
        "Arquitectura de malla plisada que expande la superficie activa hasta 3 veces respecto al cilindro liso.",
        "Totalmente regenerable y reutilizable mediante ultrasonidos, retrolavado y desgasificación térmica.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/oil-filter-cartridge-stainless-steel-pleated-filter-element.webp?v=nwo72bdda2k",
        alt: "Cartuchos filtrantes plisados y sinterizados de acero inoxidable en fabricación",
      },
    },
    specTable: {
      heading: "Parámetros dimensionales y de retención estándar",
      intro: "Especificaciones representativas de cartuchos soldados y plisados de acero inoxidable AISI 304 y 316L.",
      columns: [
        { key: "configuration", label: "Configuración del elemento", align: "left" },
        { key: "mediaType", label: "Estructura del medio", align: "left" },
        { key: "retentionRange", label: "Rango de retención (µm)", align: "right" },
        { key: "outerDiameter", label: "Diámetro exterior (mm)", align: "right" },
        { key: "standardLength", label: "Longitud estándar (mm)", align: "right" },
      ],
      rows: [
        {
          configuration: "Elemento cilíndrico liso",
          mediaType: "Malla tejida monocapa / multicapa",
          retentionRange: "20 – 250",
          outerDiameter: "25 – 150",
          standardLength: "250 – 1.000",
        },
        {
          configuration: "Elemento plisado de alta superficie",
          mediaType: "Malla holandesa / cuadrada plisada",
          retentionRange: "5 – 150",
          outerDiameter: "50 – 250",
          standardLength: "250 – 1.000",
        },
        {
          configuration: "Cartucho de malla sinterizada",
          mediaType: "Laminado sinterizado de 5 capas",
          retentionRange: "1 – 100",
          outerDiameter: "30 – 200",
          standardLength: "125 – 1.500",
        },
        {
          configuration: "Elemento con núcleo reforzado",
          mediaType: "Núcleo perforado + malla exterior",
          retentionRange: "10 – 200",
          outerDiameter: "60 – 350",
          standardLength: "500 – 2.000",
        },
      ],
      caption: "Diámetros especiales (hasta 350 mm) y longitudes a medida (hasta 3.000 mm) fabricados bajo pedido.",
    },
    features: {
      heading: "Atributos técnicos principales de los cartuchos MutualFilter",
      intro: "Normas de fabricación de precisión orientadas a la fiabilidad operativa continua.",
      items: [
        {
          title: "Núcleos estructurales rígidos",
          text: "Núcleos internos perforados de acero inoxidable de gran espesor que aseguran resistencia al colapso frente a altas presiones diferenciales.",
        },
        {
          title: "Terminales y conexiones a medida",
          text: "Juntas planas DOE, bayonetas SOE con doble junta tórica y roscas macho/hembra soldadas directamente a la estructura filtrante.",
        },
        {
          title: "Control uniforme del plisado",
          text: "Separación regular de pliegues y mallas de soporte que impiden el pinzamiento y aseguran un flujo continuo en toda la superficie.",
        },
        {
          title: "Integridad 100% metálica",
          text: "Fabricados sin adhesivos ni resinas en la zona de paso del fluido, garantizando compatibilidad con disolventes y vapor.",
        },
      ],
    },
    related: {
      heading: "Componentes filtrantes y ensamblajes relacionados",
      intro: "MutualFilter suministra ensamblajes metálicos complementarios para recipientes a presión, tuberías y extrusoras.",
      cards: [
        {
          title: "Centro de componentes filtrantes",
          text: "Gama completa de elementos fabricados en acero inoxidable, capacidades de ingeniería y dimensiones de producción.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Resumen de componentes filtrantes de acero inoxidable",
          },
          badge: "Categoría superior",
          linkLabel: "Explorar componentes",
        },
        {
          title: "Discos filtrantes y bobinas cortadas",
          text: "Discos de malla monocapa y multicapa cortados con tolerancias exactas para cambiadores de malla y cuerpos filtrantes.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Discos filtrantes estampados de acero inoxidable y bobinas cortadas",
          },
          badge: "Elemento hermano",
          linkLabel: "Ver discos filtrantes",
        },
        {
          title: "Tubos y cilindros filtrantes de precisión",
          text: "Tubos cilíndricos soldados de alta rigidez para filtros de tubería, tomas de captación y separación de fases.",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Tubos y cilindros filtrantes de acero inoxidable soldados de precisión",
          },
          badge: "Elemento hermano",
          linkLabel: "Ver tubos filtrantes",
        },
        {
          title: "Paquetes filtrantes multicapa para cambiadores",
          text: "Packs de malla soldados por puntos o con marco de aluminio para cambiadores de filtros en extrusión de polímeros.",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Paquetes filtrantes multicapa de malla metálica",
          },
          badge: "Elemento hermano",
          linkLabel: "Ver paquetes filtrantes",
        },
        {
          title: "Cestas y placas filtrantes fabricadas",
          text: "Filtros de cesta y placas planas reforzadas para clarificación de líquidos a gran caudal y filtración a presión.",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Cesta filtrante de malla de alambre de acero inoxidable",
          },
          badge: "Elemento hermano",
          linkLabel: "Ver cestas y placas",
        },
      ],
    },
    cta: {
      id: "filter-cartridges-cta",
      eyebrow: "Ingeniería de cartuchos a medida",
      heading: "Solicite especificaciones de cartuchos o presupuesto de producción",
      lead: "Indique las dimensiones requeridas, el micraje de retención, la terminación y las condiciones de su proceso para recibir una propuesta técnica.",
      secondaryLabel: "Contactar con ingeniería",
      secondaryTo: "/ContactPage",
      note: "Informes de ensayo de material (MTR) e inspección PMI disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "FilterCartridges",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Filterkomponenten",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Edelstahl-Filterkerzen",
      lead: "Präzisionsgefertigte zylindrische und plissierte Edelstahl-Filterkerzen für Hochtemperatur- und Hochdifferenzdruck-Anwendungen in der industriellen Flüssigkeits- und Gastrennung.",
      secondaryCta: {
        label: "Übersicht Filterkomponenten",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
        alt: "Präzisions-plissierte Edelstahl-Filterkerze mit geschweißten Endkappen",
      },
      cardNote: "Vollverschweißte oder gebördelte OEM-Bauformen",
      cardLink: {
        label: "Spezifikationen anfordern",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Filterfeinheit", value: "1 µm – 250 µm" },
        { label: "Konstruktion", value: "Vollverschweißt / Gebördelt" },
        { label: "Außendurchmesser", value: "25 – 350 mm" },
        { label: "Werkstoffe", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Konstruktion und Filtrationsleistung",
      heading: "Metallelemente für anspruchsvolle Industrieanwendungen",
      paragraphs: [
        "MutualFilter Edelstahl-Filterkerzen bieten widerstandsfähige, abreinigbare Filterbarrieren für Prozesse, bei denen Einwegmedien aus Polymer thermisch degradieren oder versagen würden. Gefertigt aus Drahtgewebe, Sintermetall und perforierten Stützkernen halten diese Elemente hohen Temperaturen und Differenzdrücken stand.",
        "Zwei Hauptbauformen stehen zur Verfügung: glatte zylindrische Filterrohre für schnelle Oberflächenfiltration und plissierte Kerzen, die die wirksame Filterfläche bei gleichem Bauraum um das bis zu Dreifache vergrößern. Präzise Längsnaht- und Endkappenschweißungen verhindern Bypass-Leckagen.",
        "Lieferbar in Standard- und Sonderlängen mit Anschlussvarianten wie beidseitig offen (DOE), Code 7/8 Bajonett mit O-Ringen (SOE) sowie Gewindeanschlüssen nach BSP oder NPT.",
      ],
      keyPoints: [
        "Vollverschweißte Nähte und Endkappen ohne Klebstoffe oder Harze schließen Bypass-Risiken vollständig aus.",
        "Plissierte Drahtgewebe-Geometrie vergrößert die aktive Filterfläche um das bis zu 3-fache für längere Standzeiten.",
        "Vollständig regenerierbar und wiederverwendbar durch Ultraschallreinigung, Rückspülung und thermische Pyrolyse.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/oil-filter-cartridge-stainless-steel-pleated-filter-element.webp?v=nwo72bdda2k",
        alt: "Plissierte und gesinterte Edelstahl-Filterelemente in der Fertigung",
      },
    },
    specTable: {
      heading: "Standard-Abmessungen und Abscheideparameter",
      intro: "Repräsentative technische Daten für vollverschweißte und plissierte Filterkerzen aus AISI 304 und 316L Drahtgewebe.",
      columns: [
        { key: "configuration", label: "Element-Konfiguration", align: "left" },
        { key: "mediaType", label: "Filtermedienstruktur", align: "left" },
        { key: "retentionRange", label: "Filterfeinheit (µm)", align: "right" },
        { key: "outerDiameter", label: "Außendurchmesser (mm)", align: "right" },
        { key: "standardLength", label: "Standardlänge (mm)", align: "right" },
      ],
      rows: [
        {
          configuration: "Zylindrisches Glattelement",
          mediaType: "Ein- / Mehrlagiges Drahtgewebe",
          retentionRange: "20 – 250",
          outerDiameter: "25 – 150",
          standardLength: "250 – 1.000",
        },
        {
          configuration: "Plissiertes Hochflächenelement",
          mediaType: "Plissiertes Tressengewebe / Quadratmasche",
          retentionRange: "5 – 150",
          outerDiameter: "50 – 250",
          standardLength: "250 – 1.000",
        },
        {
          configuration: "Sintergewebe-Filterkerze",
          mediaType: "5-lagiges Sintermetall-Laminat",
          retentionRange: "1 – 100",
          outerDiameter: "30 – 200",
          standardLength: "125 – 1.500",
        },
        {
          configuration: "Verstärktes Stützkern-Element",
          mediaType: "Lochblech-Kern + Drahtgewebe",
          retentionRange: "10 – 200",
          outerDiameter: "60 – 350",
          standardLength: "500 – 2.000",
        },
      ],
      caption: "Sonderdurchmesser (bis 350 mm) und Sonderlängen (bis 3.000 mm) auf Anfrage lieferbar.",
    },
    features: {
      heading: "Technische Merkmale der MutualFilter Filterkerzen",
      intro: "Präzise Fertigungsstandards für hohe Zuverlässigkeit im Dauerbetrieb.",
      items: [
        {
          title: "Robuste Innenstützkerne",
          text: "Dickwandige perforierte Innenrohre aus Edelstahl verleihen den Kerzen hohe Kollapsfestigkeit bei Druckstößen und Rückspülung.",
        },
        {
          title: "Vielfältige Endkappenformen",
          text: "DOE-Flachdichtungen, SOE-Bajonettanschlüsse mit doppelten O-Ringen und Gewindeanschlüsse werden direkt stoffschlüssig verschweißt.",
        },
        {
          title: "Gleichmäßige Faltenteilung",
          text: "Exakte Faltenabstände und integrierte Stützgewebelagen verhindern ein Zusammendrücken der Falten und sichern freien Durchfluss.",
        },
        {
          title: "100 % metallische Reinheit",
          text: "Fertigung frei von Klebstoffen oder Epoxidharzen im Strömungsweg zur Gewährleistung bester Verträglichkeit mit Lösungsmitteln und Dampf.",
        },
      ],
    },
    related: {
      heading: "Verwandte Filterkomponenten und Baugruppen",
      intro: "MutualFilter fertigt abgestimmte Filterkomponenten aus Edelstahl für Druckbehälter, Rohrleitungen und Extruder.",
      cards: [
        {
          title: "Übersicht Filterkomponenten",
          text: "Überblick über unser Programm konfektionierter Filterelemente, Fertigungsmöglichkeiten und Spezifikationen.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Konfektionierte Filterkomponenten und Baugruppen",
          },
          badge: "Übergeordnete Kategorie",
          linkLabel: "Komponenten ansehen",
        },
        {
          title: "Präzisions-Filterronden & Spaltbänder",
          text: "Ein- und mehrlagige Drahtgeweberonden und Spaltbänder nach engsten Toleranzen gestanzt für Siebwechsler und Filtergehäuse.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Präzisionsgestanzte Edelstahl-Filterronden und Drahtgewebe-Spaltbänder",
          },
          badge: "Geschwister-Element",
          linkLabel: "Ronden & Spaltbänder",
        },
        {
          title: "Präzisions-Filterrohre & Zylinderelemente",
          text: "Formstabile geschweißte Filterrohre und Zylinder für Rohrleitungsfilter, Brunnenfassungen und Phasentrennung.",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Präzisionsgeschweißte Edelstahl-Filterrohre und -zylinder",
          },
          badge: "Geschwister-Element",
          linkLabel: "Filterrohre ansehen",
        },
        {
          title: "Mehrlagen-Siebpakete für Extruder",
          text: "Punktgeschweißte, alugefasste und gesinterte Drahtgewebepakete für Siebwechsler und Polymerschmelzen.",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Mehrlagen-Drahtgewebepaket für Extruder-Siebwechsler",
          },
          badge: "Geschwister-Element",
          linkLabel: "Siebpakete ansehen",
        },
        {
          title: "Präzisions-Filterkörbe & Filterplatten",
          text: "Robuste Korbsiebe und flache Filterplatten für hohe Durchsätze in Flüssigkeitsklärung und Druckfiltration.",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Drahtgewebe-Filterkorb für Druckbehälter-Siebe",
          },
          badge: "Geschwister-Element",
          linkLabel: "Körbe & Platten ansehen",
        },
      ],
    },
    cta: {
      id: "filter-cartridges-cta",
      eyebrow: "Kerzenfertigung nach Maß",
      heading: "Spezifikationen oder Fertigungsangebot für Filterkerzen anfragen",
      lead: "Senden Sie uns Abmessungen, gewünschte Filterfeinheit, Endkappenform und Prozessparameter für eine technische Prüfung.",
      secondaryLabel: "Technikteam kontaktieren",
      secondaryTo: "/ContactPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Werkstoffanalysen auf Bestellung lieferbar.",
    },
  },
  fr: {
    breadcrumbKey: "FilterCartridges",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Composants de filtration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Cartouches filtrantes en acier inoxydable",
      lead: "Cartouches filtrantes cylindriques et plissées en acier inoxydable, conçues pour les procédés industriels sévères sous haute température et pression différentielle élevée.",
      secondaryCta: {
        label: "Pôle composants filtrants",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
        alt: "Élément filtrant cylindrique plissé en acier inoxydable avec embouts soudés",
      },
      cardNote: "Conceptions OEM entièrement soudées ou serties",
      cardLink: {
        label: "Demander les spécifications",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Seuil de filtration", value: "1 µm – 250 µm" },
        { label: "Construction", value: "Mécano-soudé / Sertissage" },
        { label: "Diamètre extérieur", value: "25 – 350 mm" },
        { label: "Nuances d'alliages", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Architecture et performances des cartouches",
      heading: "Cartouches métalliques pour conditions de filtration industrielles exigeantes",
      paragraphs: [
        "Les cartouches en acier inoxydable MutualFilter constituent des barrières de filtration fiables et régénérables pour les procédés où les médias filtrants polymères jetables se détériorent ou s'écrasent. Conçues à partir de toiles métalliques tissées, de toiles frittées et de noyaux perforés renforcés, elles supportent des contraintes thermiques et mécaniques rigoureuses.",
        "Deux architectures sont proposées : des tubes lisses cylindriques pour une filtration de surface rapide, et des éléments plissés augmentant la surface active jusqu'à 3 fois dans le même corps de filtre. Les soudures longitudinales et l'assemblage étanche des embouts préviennent toute fuite de contournement.",
        "Disponibles en longueurs standards ou sur plan, avec raccordements DOE (double extrémité ouverte), baïonnette SOE avec joints toriques et raccords filetés BSP ou NPT.",
      ],
      keyPoints: [
        "Construction mécano-soudée intégrale éliminant résines et adhésifs pour un risque de by-pass nul.",
        "Géométrie plissée multipliant la surface de filtration active jusqu'à 3 fois par rapport à un cylindre lisse.",
        "Entièrement régénérable et réutilisable par ultrasons, rétro-lavage ou pyrolyse thermique.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/oil-filter-cartridge-stainless-steel-pleated-filter-element.webp?v=nwo72bdda2k",
        alt: "Cartouches filtrantes plissées et frittées en acier inoxydable en atelier",
      },
    },
    specTable: {
      heading: "Paramètres dimensionnels et de coupure standards",
      intro: "Spécifications représentatives des cartouches mécano-soudées et plissées en acier inoxydable AISI 304 et 316L.",
      columns: [
        { key: "configuration", label: "Configuration de l'élément", align: "left" },
        { key: "mediaType", label: "Milieu filtrant", align: "left" },
        { key: "retentionRange", label: "Seuil de coupure (µm)", align: "right" },
        { key: "outerDiameter", label: "Diamètre extérieur (mm)", align: "right" },
        { key: "standardLength", label: "Longueur standard (mm)", align: "right" },
      ],
      rows: [
        {
          configuration: "Élément cylindrique lisse",
          mediaType: "Toile tissée mono / multicouche",
          retentionRange: "20 – 250",
          outerDiameter: "25 – 150",
          standardLength: "250 – 1 000",
        },
        {
          configuration: "Élément plissé haute surface",
          mediaType: "Toile hollandaise / carrée plissée",
          retentionRange: "5 – 150",
          outerDiameter: "50 – 250",
          standardLength: "250 – 1 000",
        },
        {
          configuration: "Cartouche en toile frittée",
          mediaType: "Laminé fritté 5 couches",
          retentionRange: "1 – 100",
          outerDiameter: "30 – 200",
          standardLength: "125 – 1 500",
        },
        {
          configuration: "Élément renforcé à noyau",
          mediaType: "Noyau perforé + toile enveloppante",
          retentionRange: "10 – 200",
          outerDiameter: "60 – 350",
          standardLength: "500 – 2 000",
        },
      ],
      caption: "Diamètres spécifiques (jusqu'à 350 mm) et longueurs sur mesure (jusqu'à 3 000 mm) réalisables sur demande.",
    },
    features: {
      heading: "Caractéristiques d'ingénierie des cartouches MutualFilter",
      intro: "Normes rigoureuses de fabrication pour une régularité de service et des cycles d'entretien espacés.",
      items: [
        {
          title: "Noyaux structurels rigides",
          text: "Noyaux internes en tôle perforée épaisse conférant une excellente résistance à l'écrasement sous forte pression différentielle.",
        },
        {
          title: "Embouts terminaux sur mesure",
          text: "Embouts DOE avec joints plats, baïonnettes SOE à double joint torique et raccords filetés soudés directement au média.",
        },
        {
          title: "Plissage régulier et contrôlé",
          text: "Pas de plis maîtrisé avec toiles de soutien empêchant l'écrasement des plis et assurant un écoulement fluide et régulier.",
        },
        {
          title: "Intégrité 100 % métallique",
          text: "Aucun polymère ni colle dans le flux liquide pour une compatibilité totale avec solvants, vapeur et acides.",
        },
      ],
    },
    related: {
      heading: "Composants de filtration associés et ensembles fabriqués",
      intro: "MutualFilter propose un ensemble complet de composants mécano-soudés en inox pour corps de filtres, tuyauteries et changeurs de filtres.",
      cards: [
        {
          title: "Pôle composants filtrants",
          text: "Aperçu général de notre gamme de pièces usinées en toile métallique et capacités de fabrication sur mesure.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Vue d'ensemble des composants filtrants mécano-soudés",
          },
          badge: "Catégorie parente",
          linkLabel: "Explorer les composants",
        },
        {
          title: "Disques découpés et bandes refendues",
          text: "Disques en toile monocouche ou multicouche et bandes refendues continues découpés selon des tolérances précises.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Disques filtrants découpés et bandes de toile métallique en acier inoxydable",
          },
          badge: "Élément associé",
          linkLabel: "Voir les disques",
        },
        {
          title: "Tubes et cylindres de filtration",
          text: "Tubes filtrants soudés longitudinalement et cylindres de filtration pour tuyauteries industrielles et séparation de phases.",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Tubes et cylindres filtrants soudés de précision en acier inoxydable",
          },
          badge: "Élément associé",
          linkLabel: "Voir les tubes",
        },
        {
          title: "Packs filtrants multicouches",
          text: "Packs de toiles métalliques multicouches soudés par points ou bordés d'aluminium pour changeurs de filtres d'extrusion.",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Packs de toiles métalliques multicouches pour changeurs de filtres",
          },
          badge: "Élément associé",
          linkLabel: "Voir les packs",
        },
        {
          title: "Paniers filtrants et plateaux usinés",
          text: "Paniers filtrants renforcés et plateaux de filtration plats pour clarification de liquides et filtration sous pression.",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Panier filtrant en toile métallique inox de précision",
          },
          badge: "Élément associé",
          linkLabel: "Voir paniers et plateaux",
        },
      ],
    },
    cta: {
      id: "filter-cartridges-cta",
      eyebrow: "Fabrication de cartouches sur mesure",
      heading: "Demandez vos spécifications de cartouches ou un devis de fabrication",
      lead: "Précisez vos dimensions requises, le seuil de rétention, le type d'embout et les conditions de service pour analyse technique.",
      secondaryLabel: "Contacter l'équipe technique",
      secondaryTo: "/ContactPage",
      note: "Certificats matière (MTR) et contrôle PMI disponibles sur spécification à la commande.",
    },
  },
  pt: {
    breadcrumbKey: "FilterCartridges",
    relatedTopic: "components",
    hero: {
      categoryBadge: "Componentes de filtração",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Cartuchos filtrantes de aço inoxidável",
      lead: "Cartuchos filtrantes cilíndricos e plissados de aço inoxidável projetados para sistemas industriais severos de separação líquida e gasosa em altas temperaturas e pressões diferenciais.",
      secondaryCta: {
        label: "Central de componentes",
        to: "/FilterComponentsPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/Filter-Cartridges.jpg",
        alt: "Elemento filtrante cilíndrico plissado de aço inoxidável com tampas soldadas",
      },
      cardNote: "Construções totalmente soldadas ou cravadas OEM",
      cardLink: {
        label: "Solicitar especificações",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Faixa de micragem", value: "1 µm – 250 µm" },
        { label: "Construção", value: "Totalmente soldado / Cravado" },
        { label: "Diâmetro externo", value: "25 – 350 mm" },
        { label: "Ligas de aço", value: "AISI 304 / 316L" },
      ],
    },
    overview: {
      eyebrow: "Arquitetura e desempenho dos cartuchos",
      heading: "Cartuchos metálicos desenvolvidos para processos industriais rigorosos",
      paragraphs: [
        "Os cartuchos de aço inoxidável MutualFilter proporcionam barreiras de filtração resistentes e regeneráveis para processos em que elementos descartáveis poliméricos sofrem degradação ou colapso mecânico. Fabricados exclusivamente com malha tecida, tela sinterizada e núcleos estruturais perfurados, resistem a variações térmicas e fortes pressões.",
        "Estão disponíveis em duas configurações principais: elementos cilíndricos lisos para retenção de sólidos superficiais e cartuchos plissados que ampliam a área filtrante em até 3 vezes dentro do mesmo vaso. A solda longitudinal contínua e a vedação por fusão das tampas eliminam riscos de desvio de fluxo.",
        "Fabricados em comprimentos industriais padrão e medidas customizadas, com terminais DOE (aberto em ambas as extremidades), baionetas SOE com anéis de vedação e roscas BSP ou NPT.",
      ],
      keyPoints: [
        "Construção totalmente soldada por fusão que elimina adesivos e epóxi, anulando riscos de bypass.",
        "Arquitetura de malha plissada que expande a área de filtração ativa em até 3 vezes em relação ao cilindro liso.",
        "Totalmente reutilizável e regenerável por ultrassom, retrolavagem e queima térmica.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/oil-filter-cartridge-stainless-steel-pleated-filter-element.webp?v=nwo72bdda2k",
        alt: "Cartuchos filtrantes plissados e sinterizados de aço inoxidável em fabricação",
      },
    },
    specTable: {
      heading: "Parâmetros dimensionais e de retenção padrão",
      intro: "Especificações técnicas representativas de cartuchos soldados e plissados de aço inoxidável AISI 304 e 316L.",
      columns: [
        { key: "configuration", label: "Configuração do elemento", align: "left" },
        { key: "mediaType", label: "Meio filtrante", align: "left" },
        { key: "retentionRange", label: "Faixa de retenção (µm)", align: "right" },
        { key: "outerDiameter", label: "Diâmetro externo (mm)", align: "right" },
        { key: "standardLength", label: "Comprimento padrão (mm)", align: "right" },
      ],
      rows: [
        {
          configuration: "Elemento cilíndrico liso",
          mediaType: "Malha tecida camada única / múltipla",
          retentionRange: "20 – 250",
          outerDiameter: "25 – 150",
          standardLength: "250 – 1.000",
        },
        {
          configuration: "Elemento plissado de alta área",
          mediaType: "Malha holandesa / quadrada plissada",
          retentionRange: "5 – 150",
          outerDiameter: "50 – 250",
          standardLength: "250 – 1.000",
        },
        {
          configuration: "Cartucho de malha sinterizada",
          mediaType: "Laminado sinterizado de 5 camadas",
          retentionRange: "1 – 100",
          outerDiameter: "30 – 200",
          standardLength: "125 – 1.500",
        },
        {
          configuration: "Elemento com núcleo reforçado",
          mediaType: "Núcleo perfurado + malha envoltória",
          retentionRange: "10 – 200",
          outerDiameter: "60 – 350",
          standardLength: "500 – 2.000",
        },
      ],
      caption: "Diâmetros sob encomenda (até 350 mm) e comprimentos especiais (até 3.000 mm) disponíveis sob consulta.",
    },
    features: {
      heading: "Características de engenharia dos cartuchos MutualFilter",
      intro: "Padrões rigorosos de fabricação para alta confiabilidade operacional e intervalos estendidos de manutenção.",
      items: [
        {
          title: "Núcleos estruturais reforçados",
          text: "Tubos internos perfurados de chapa espessa conferem excelente resistência ao colapso mecânico sob forte diferencial de pressão.",
        },
        {
          title: "Terminais e conexões especiais",
          text: "Terminais planos DOE, encaixes baioneta SOE com anéis de vedação e roscas soldadas diretamente ao elemento filtrante.",
        },
        {
          title: "Controle uniforme de pregas",
          text: "Espaçamento preciso de pregas e telas metálicas de apoio que evitam o colapso do canal e asseguram fluxo equilibrado.",
        },
        {
          title: "Integridade 100% metálica",
          text: "Sem compostos poliméricos ou adesivos no canal de passagem do fluido, permitindo uso com solventes agressivos e vapor.",
        },
      ],
    },
    related: {
      heading: "Componentes filtrantes e conjuntos relacionados",
      intro: "A MutualFilter fornece conjuntos complementares em aço inoxidável para vasos de processo, tubulações e extrusoras.",
      cards: [
        {
          title: "Central de componentes filtrantes",
          text: "Visão geral da linha de elementos fabricados em tela de aço inoxidável, capacidades de usinagem e dimensões de produção.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/filter-element.jpg",
            alt: "Visão geral de componentes filtrantes de aço inoxidável",
          },
          badge: "Categoria superior",
          linkLabel: "Explorar componentes",
        },
        {
          title: "Discos filtrantes e rolos cortados",
          text: "Discos de malha de camada única e múltipla e rolos estreitos estampados com tolerâncias exatas para trocadores de telas.",
          to: "/SlitCoilsAndStampedFilterDiscsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-stamped-stain-2c6f88.png",
            alt: "Discos de filtro de aço inoxidável estampados e rolos de malha de arame cortados",
          },
          badge: "Elemento irmão",
          linkLabel: "Ver discos filtrantes",
        },
        {
          title: "Tubos e cilindros filtrantes de precisão",
          text: "Tubos cilíndricos soldados de alta rigidez para filtros de tubulação, captação de água e separação de fases.",
          to: "/FilterTubesAndCylindersPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-stain-616af2.png",
            alt: "Tubos e cilindros filtrantes em aço inoxidável soldados com precisão",
          },
          badge: "Elemento irmão",
          linkLabel: "Ver tubos filtrantes",
        },
        {
          title: "Conjuntos de pacotes filtrantes multicamadas",
          text: "Pacotes de malha soldados por pontos ou com aro de alumínio para trocadores de tela em extrusão de polímeros.",
          to: "/ScreenPacksPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-7f27c4.png",
            alt: "Pacotes filtrantes multicamadas para troca-filtros de extrusão",
          },
          badge: "Elemento irmão",
          linkLabel: "Ver pacotes filtrantes",
        },
        {
          title: "Cestos e placas filtrantes fabricados",
          text: "Filtros de cesto reforçados e placas planas para clarificação de líquidos em alta vazão e filtração sob pressão.",
          to: "/FilterBasketsAndPlatesPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-sta-d6091c.png",
            alt: "Cesto filtrante em malha de arame de aço inoxidável de precisão",
          },
          badge: "Elemento irmão",
          linkLabel: "Ver cestos e placas",
        },
      ],
    },
    cta: {
      id: "filter-cartridges-cta",
      eyebrow: "Engenharia de cartuchos sob medida",
      heading: "Solicite especificações de cartuchos ou cotação de produção",
      lead: "Informe as dimensões necessárias, faixa de micragem, conexões e parâmetros operacionais para análise da nossa equipe técnica.",
      secondaryLabel: "Fale com a equipe de engenharia",
      secondaryTo: "/ContactPage",
      note: "Relatórios de teste de materiais (MTR) e inspeção PMI disponíveis sob especificação no pedido.",
    },
  },
};
